import {
  AllUnitsDocument,
  useDeleteUnitMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {FullUnitFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type DeleteUnitModalUserProps = {
  unit: FullUnitFragment;
  onDeleted?: () => void;
};

/**
 * Deletion has no force option: accounts and raw transactions store the
 * unit's id, and a dangling id has no display content behind it. The server
 * refuses while anything references the unit; the error is surfaced as-is.
 */
export const DeleteUnitModal = (
  props: DeleteUnitModalUserProps & SystemProps,
) => {
  const {unit, onRequestClose, onDeleted} = props;
  const [deleteUnit, {loading}] = useDeleteUnitMutation({
    refetchQueries: [{query: AllUnitsDocument}],
  });
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteUnit({variables: {id: unit._id}});
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete unit"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to delete <strong>{unit.name}</strong>? This
        action cannot be undone. Deletion is refused while any account or
        transaction is denominated in this unit.
      </p>
    </Modal>
  );
};
